import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, CheckCircle, Loader2, Mail, AlertTriangle, Shield, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import PageSEO from "@/components/seo/PageSEO";
import TitleHoldersStep, { type TitleHolder } from "@/components/consumer/funnel/TitleHoldersStep";
import PropertyDetailsStep, { type PropertyDetails } from "@/components/consumer/funnel/PropertyDetailsStep";
import ReviewConfirmStep from "@/components/consumer/funnel/ReviewConfirmStep";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useSecureAuth } from "@/hooks/useSecureAuth";

import { safeStorage } from "@/utils/safariStorage";

const STEPS = [
  { label: "Title Holders", number: 1 },
  { label: "Create Account", number: 2 },
  { label: "Property Details", number: 3 },
  { label: "Review & Submit", number: 4 },
  { label: "Thank You", number: 5 },
];

const ConsumerGetStartedPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { secureSignUp, isLoading: authLoading } = useSecureAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [titleHolders, setTitleHolders] = useState<TitleHolder[]>([
    { full_name: "", email: "", phone_number: "" },
  ]);
  
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails | null>(null);

  // Account creation state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [verificationExpired, setVerificationExpired] = useState(false);
  

  const progressValue = (currentStep / STEPS.length) * 100;

  // On mount: check if resuming after email verification
  useEffect(() => {
    const verified = searchParams.get("verified");
    const errorParam = searchParams.get("error");
    const errorDesc = searchParams.get("error_description");

    // Handle URL error params (e.g. expired verification link)
    if (errorParam) {
      setVerificationExpired(true);
      setCurrentStep(2);
      setAwaitingVerification(false);
      console.warn("Verification error:", errorParam, errorDesc);
      return;
    }

    if (verified === "true") {
      setIsResuming(true);
      let isSubscribed = true;

      // Immediate session check
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session && isSubscribed) {
          isSubscribed = false;
          resumeAfterVerification(session.user.email);
        }
      });

      // Also listen for auth state changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
        if (session && isSubscribed) {
          isSubscribed = false;
          resumeAfterVerification(session.user.email);
          subscription.unsubscribe();
        }
      });

      // Timeout fallback
      const timer = setTimeout(() => {
        if (isSubscribed) {
          setIsResuming(false);
          setVerificationExpired(true);
          setCurrentStep(2);
          setAwaitingVerification(false);
        }
      }, 15000);

      return () => {
        isSubscribed = false;
        clearTimeout(timer);
        subscription.unsubscribe();
      };
    }
  }, [searchParams]);

  const resumeAfterVerification = (email: string | undefined) => {
    if (!email) {
      setIsResuming(false);
      return;
    }

    // Restore title holders from localStorage
    const storedTitleHolders = safeStorage.getItem(`consumer_title_holders_${email}`);
    if (storedTitleHolders) {
      try {
        setTitleHolders(JSON.parse(storedTitleHolders));
      } catch {}
    }

    // Clean up localStorage
    safeStorage.removeItem(`consumer_title_holders_${email}`);

    setCurrentStep(3);
    setIsResuming(false);
  };

  // Step 1: Advance to account creation (email validation is handled in TitleHoldersStep)
  const handleStep1Next = () => {
    setCurrentStep(2);
  };

  // Step 2: Create account
  const passwordsMatch = password === confirmPassword;
  const passwordValid = password.length >= 8 && passwordsMatch;

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordValid) return;

    const primaryHolder = titleHolders[0];
    const email = primaryHolder.email;

    // Store title holders keyed by email so they survive email verification redirect
    safeStorage.setItem(`consumer_title_holders_${email}`, JSON.stringify(titleHolders));

    const { error } = await secureSignUp(email, password, {
      user_type: "consumer",
      full_name: primaryHolder.full_name,
      phone_number: primaryHolder.phone_number,
    }, `${window.location.origin}/consumer/get-started?verified=true`);

    if (!error) {
      setAwaitingVerification(true);
    }
  };

  // Step 3: Property details (pure client-side)
  const handleStep3Next = (details: PropertyDetails) => {
    setPropertyDetails(details);
    setCurrentStep(4);
  };

  if (isResuming) {
    return (
      <div className="min-h-screen bg-[#080c14] flex items-center justify-center text-white">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-[#ffd700] mx-auto" />
          <p className="text-slate-400">Verifying your email and resuming...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080c14] text-white">
      <PageSEO
        title="Get Started - Market Your Home Free | GoDyspo"
        description="Start the process to get your home marketed to serious cash buyers at no cost."
        canonical="/consumer/get-started"
      />

      {/* Header */}
      <header className="border-b border-border/30 bg-[#080c14]/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <Link to="/consumer" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </Link>
          <Link to="/consumer" className="text-xl font-bold text-[#ffd700]">GoDyspo</Link>
          <div className="w-16" />
        </div>
      </header>

      {/* Progress */}
      <div className="container mx-auto max-w-3xl px-4 pt-8">
        <div className="mb-2">
          <Progress value={progressValue} className="h-2 bg-muted" />
        </div>
        <div className="flex justify-between mb-10">
          {STEPS.map((step) => (
            <div
              key={step.number}
              className={`flex items-center gap-1.5 text-xs sm:text-sm ${
                step.number <= currentStep ? "text-[#ffd700]" : "text-muted-foreground"
              }`}
            >
              {step.number < currentStep ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <span className={`flex items-center justify-center h-5 w-5 rounded-full text-xs font-bold border ${
                  step.number === currentStep
                    ? "border-[#ffd700] text-[#ffd700]"
                    : "border-muted-foreground text-muted-foreground"
                }`}>
                  {step.number}
                </span>
              )}
              <span className="hidden sm:inline">{step.label}</span>
            </div>
          ))}
        </div>

        {/* Step 1: Title Holders */}
        {currentStep === 1 && (
          <TitleHoldersStep
            titleHolders={titleHolders}
            onChange={setTitleHolders}
            onNext={handleStep1Next}
          />
        )}

        {/* Step 2: Create Account */}
        {currentStep === 2 && !awaitingVerification && !verificationExpired && (
          <div className="max-w-md mx-auto">
            <h2 className="text-2xl font-bold mb-2">Create Your Account</h2>
            <p className="text-slate-400 mb-6 text-sm">
              Create an account to track your agreement and access your dashboard. We'll use{" "}
              <span className="text-white font-medium">{titleHolders[0]?.email}</span> as your login email.
            </p>
            <form onSubmit={handleCreateAccount} className="space-y-4">
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Create a password"
                  minLength={8}
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Confirm your password"
                  minLength={8}
                />
                {confirmPassword && (
                  <div className={`flex items-center gap-1.5 mt-1 text-sm animate-fade-in ${passwordsMatch ? 'text-green-500' : 'text-destructive'}`}>
                    {passwordsMatch ? (
                      <>
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Passwords match</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4" />
                        <span>Passwords do not match</span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(1)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={authLoading || !passwordValid}
                  className="flex-1 bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] font-semibold"
                >
                  {authLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: Awaiting email verification */}
        {currentStep === 2 && awaitingVerification && (
          <div className="text-center py-12 max-w-md mx-auto space-y-6">
            <Mail className="h-16 w-16 text-[#ffd700] mx-auto" />
            <h2 className="text-2xl font-bold">Check Your Email</h2>
            <p className="text-slate-400 leading-relaxed">
              We've sent a verification link to{" "}
              <span className="text-white font-medium">{titleHolders[0]?.email}</span>.
              Click the link in the email to verify your account and continue setting up your property details.
            </p>
            <div className="bg-muted/20 border border-border/40 rounded-lg p-4 text-left space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-[#ffd700] mt-0.5 shrink-0" />
                <p className="text-xs text-slate-400 leading-relaxed">
                  <span className="text-slate-300 font-medium">Tip:</span> For best results, open the verification link in your default browser (Safari, Chrome, etc.) — not from within your email app's built-in browser.
                </p>
              </div>
            </div>
            <p className="text-slate-500 text-sm">
              Didn't receive it? Check your spam folder or wait a moment.
            </p>
          </div>
        )}

        {/* Step 2: Verification expired / failed */}
        {currentStep === 2 && verificationExpired && (
          <div className="text-center py-12 max-w-md mx-auto space-y-6">
            <AlertTriangle className="h-16 w-16 text-[#ffd700] mx-auto" />
            <h2 className="text-2xl font-bold">Verification Link Expired</h2>
            <p className="text-slate-400 leading-relaxed">
              Your verification link has expired or was opened in an unsupported browser. Please log in to continue where you left off.
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/consumer/login">
                <Button className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] w-full" size="lg">
                  Log In to Continue
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => {
                  setVerificationExpired(false);
                  setCurrentStep(1);
                }}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Start New Registration
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Property Details */}
        {currentStep === 3 && (
          <PropertyDetailsStep
            initialDetails={propertyDetails || undefined}
            onBack={() => setCurrentStep(2)}
            onNext={handleStep3Next}
          />
        )}

        {/* Step 4: Review & Submit */}
        {currentStep === 4 && propertyDetails && (
          <ReviewConfirmStep
            titleHolders={titleHolders}
            propertyDetails={propertyDetails}
            onBack={() => setCurrentStep(3)}
            onNext={() => setCurrentStep(5)}
            onEditStep={(step) => {
              if (step === 1) setCurrentStep(1);
              else if (step === 2) setCurrentStep(3);
            }}
          />
        )}

        {/* Step 5: Thank You */}
        {currentStep === 5 && (
          <div className="text-center py-16 space-y-6">
            <CheckCircle className="h-20 w-20 text-[#ffd700] mx-auto" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Thank you for marketing your property with GoDyspo!
            </h2>
            <p className="text-slate-400 max-w-md mx-auto text-lg leading-relaxed">
              A representative will reach out to you shortly to complete the next step.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
              <Link to="/consumer/dashboard">
                <Button className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] px-8" size="lg">
                  View Dashboard
                </Button>
              </Link>
              <Link to="/consumer">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 px-8" size="lg">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsumerGetStartedPage;